import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { AUTH } from "@/constants/testIds";
import { Flame, User as UserIcon, Award } from "lucide-react";
import { toast } from "sonner";
import GoogleSignInButton from "@/components/GoogleSignInButton";

export default function Signup() {
  const { signup } = useAuth();
  const navigate = useNavigate();
  const [role, setRole] = useState("client");
  const [first_name, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [coachCode, setCoachCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();

  // Affiliate attribution: ?ref=CODE on any entry page (or the /r/CODE
  // shortlink) is stashed in localStorage so it survives navigation.
  useEffect(() => {
    const ref = searchParams.get("ref");
    if (ref) localStorage.setItem("fp_ref_code", ref.toUpperCase());
  }, [searchParams]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { first_name, email, password, role };
      if (role === "client" && coachCode.trim()) {
        payload.coach_code = coachCode.trim().toUpperCase();
      }
      const refCode = localStorage.getItem("fp_ref_code");
      if (refCode) payload.ref_code = refCode;
      const u = await signup(payload);
      localStorage.removeItem("fp_ref_code");
      toast.success("Account created — let's add your card");
      // Every new account is funnelled through the card-on-file screen
      // before they can use the app. Trial is gated to prevent fly-by signups.
      navigate("/start-trial");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Signup failed");
    } finally {
      setLoading(false);
    }
  };

  const RoleBtn = ({ value, icon: Icon, label, sub, testid }) => (
    <button
      type="button"
      data-testid={testid}
      onClick={() => setRole(value)}
      className={`flex-1 text-left p-4 rounded-sm border transition-colors ${
        role === value
          ? "border-blaze bg-obsidian-900 ring-1 ring-blaze"
          : "border-obsidian-600 hover:border-blaze/60"
      }`}
    >
      <Icon className={`h-5 w-5 mb-2 ${role === value ? "text-blaze" : "text-zinc-400"}`} />
      <div className="font-heading text-lg uppercase">{label}</div>
      <div className="text-[11px] text-zinc-500 uppercase tracking-widest mt-1">{sub}</div>
    </button>
  );

  return (
    <div className="min-h-screen bg-obsidian-900 flex flex-col">
      <header className="border-b border-obsidian-600 px-6 py-4">
        <Link to="/" className="flex items-center gap-2">
          <img src="/icon-192.png" alt="Ripple Effect" className="h-6 w-6 rounded-sm" />
          <span className="font-heading text-xl">RIPPLE<span className="text-blaze"> EFFECT</span></span>
        </Link>
      </header>
      <main className="flex-1 grid place-items-center px-5 py-10">
        <form onSubmit={submit} className="w-full max-w-md obs-card">
          <div className="overline text-blaze mb-2">/ Begin</div>
          <h1 className="font-heading text-4xl uppercase mb-5">Build your program.</h1>

          <div className="overline mb-2">I am a</div>
          <div className="flex gap-3 mb-5">
            <RoleBtn value="client" icon={UserIcon} label="Athlete" sub="Train · track · improve" testid="role-client" />
            <RoleBtn value="coach" icon={Award} label="Coach" sub="Manage clients · build programs" testid="role-coach" />
          </div>

          <label className="block mb-4">
            <div className="overline mb-1">First name</div>
            <input data-testid={AUTH.signupFirstName} required value={first_name} onChange={(e) => setFirstName(e.target.value)} className="w-full bg-obsidian-900 border border-obsidian-600 px-3 py-2.5 rounded-sm focus:border-blaze focus:outline-none focus:ring-1 focus:ring-blaze" />
          </label>
          <label className="block mb-4">
            <div className="overline mb-1">Email</div>
            <input data-testid={AUTH.signupEmail} type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="w-full bg-obsidian-900 border border-obsidian-600 px-3 py-2.5 rounded-sm focus:border-blaze focus:outline-none focus:ring-1 focus:ring-blaze" />
          </label>
          <label className="block mb-4">
            <div className="overline mb-1">Password (min 6)</div>
            <input data-testid={AUTH.signupPassword} type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} className="w-full bg-obsidian-900 border border-obsidian-600 px-3 py-2.5 rounded-sm focus:border-blaze focus:outline-none focus:ring-1 focus:ring-blaze" />
          </label>

          {role === "client" && (
            <label className="block mb-6">
              <div className="overline mb-1">Coach code <span className="text-zinc-600 normal-case">(optional)</span></div>
              <input
                data-testid="signup-coach-code"
                value={coachCode}
                onChange={(e) => setCoachCode(e.target.value.toUpperCase())}
                placeholder="ABCDE123"
                maxLength={12}
                className="w-full bg-obsidian-900 border border-obsidian-600 px-3 py-2.5 rounded-sm font-mono uppercase tracking-widest focus:border-blaze focus:outline-none focus:ring-1 focus:ring-blaze"
              />
            </label>
          )}

          <button data-testid={AUTH.signupSubmit} disabled={loading} className="blaze-btn w-full py-3 text-sm disabled:opacity-50">
            {loading ? "Creating..." : role === "coach" ? "Become a coach" : "Create account"}
          </button>

          {role !== "coach" && <GoogleSignInButton label="Sign up with Google" />}

          <div className="mt-6 text-sm text-zinc-400 text-center">
            Already have an account?{" "}
            <Link to="/auth/login" data-testid={AUTH.toLogin} className="text-blaze hover:text-blaze-hover">Log in</Link>
          </div>
        </form>
      </main>
    </div>
  );
}
